import { formatMarginRequirement } from '@/helpers/ohoHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { removeSubscription, TraderMarket } from '@/state/reducers/traderMarketSlice';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { Option } from '../trader-modules/Tickers/InstrumentSelector';
import Divider from './Divider';
import { findInstrumentInfo, GeneralInfo, InstrumentInfoTab, TradeInfo, useInstrumentInfoTabs } from './InstrumentInfo';
import TabFilters from './TabFilters';

export const formConfig = {
    offsetOverride: { mainAxis: 2, crossAxis: -151 }
};

interface InstrumentInfoContentProps {
    option?: Option;
    watchListId?: string;
    flexWrap?: boolean;
    onClose: () => void;
    onRemove?: (e: TraderMarket) => void;
    onSubmit: (e: TraderMarket) => void;
}

const InstrumentInfoContent = (props: InstrumentInfoContentProps) => {
    const { option, watchListId, onClose, onRemove, onSubmit } = props;
    const { market } = option || {};

    const dispatch = useAppDispatch();
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);
    const numberFormatter = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const lang = useLanguage();
    const instrumentInfoContentTabs = useInstrumentInfoTabs();

    const { jwt, instrumentsIncludeRatioApi, entityType } = useSso();
    const { data: instruments } = instrumentsIncludeRatioApi;

    const isXplorSpot = !location.pathname.includes('/trader');

    const [instrumentInfoContentTab, setInstrumentInfoContentTab] = useState(InstrumentInfoTab.General);

    const [ccy1, ccy2] = useMemo(() => market?.celer.split('/') || [], [market?.celer]);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const publicInstrumentConfig = useMemo(() => {
        return getInstrumentConfig(accountType, instrumentConfigs, market?.celer);
    }, [market, instrumentConfigs, accountType]);

    const detailedInstrumentConfig = useMemo(() => {
        return instruments?.find((instrument) => {
            return findInstrumentInfo(isXplorSpot, publicInstrumentConfig, instrument, market);
        });
    }, [publicInstrumentConfig, instruments, market]);
    const underlyingCcy = useCurrency(detailedInstrumentConfig?.underlying || '');

    const marginRequirement = useMemo(() => {
        return formatMarginRequirement(detailedInstrumentConfig?.marginRequirement, numberFormatter);
    }, [detailedInstrumentConfig]);

    const { isCFD, isRollingFiatFX } = useMemo(
        () => ({
            isCFD: detailedInstrumentConfig?.type === 'CFD',
            isRollingFiatFX: detailedInstrumentConfig?.type === 'FX' && detailedInstrumentConfig?.subType === 'Spot',
            isCryptoSpot: detailedInstrumentConfig?.type === 'Crypto' && detailedInstrumentConfig?.subType === 'Spot'
        }),
        [detailedInstrumentConfig]
    );

    useEffect(() => {
        if (!jwt || !detailedInstrumentConfig) setInstrumentInfoContentTab(InstrumentInfoTab['Trade Info']);
        else setInstrumentInfoContentTab(InstrumentInfoTab.General);
    }, [jwt, detailedInstrumentConfig]);

    return (
        <Fragment>
            <div className="flex flex-col h-full text-brand-text">
                <div className="flex flex-col flex-1 basis-0 bg-brand-background-dark">
                    <TabFilters.Main className="">
                        {jwt && detailedInstrumentConfig && (
                            <TabFilters.Filter
                                className="!text-xs !h-[32px]"
                                selectedFilter={instrumentInfoContentTab}
                                setSelectedFilter={setInstrumentInfoContentTab}
                                filter={instrumentInfoContentTabs[0]}
                            />
                        )}
                        <TabFilters.Filter
                            className="!text-xs !h-[32px]"
                            selectedFilter={instrumentInfoContentTab}
                            setSelectedFilter={setInstrumentInfoContentTab}
                            filter={instrumentInfoContentTabs[1]}
                        />
                    </TabFilters.Main>
                    <div className="h-full">
                        {jwt && detailedInstrumentConfig && instrumentInfoContentTab === InstrumentInfoTab.General && (
                            <div
                                className={classNames('flex p-2 pt-1 justify-between w-fit', {
                                    ['flex-wrap gap-1']: props.flexWrap,
                                    ['flex-wrap xsm:flex-nowrap gap-1 xsm:gap-3']: !props.flexWrap
                                })}>
                                <GeneralInfo
                                    market={market}
                                    detailedInstrumentConfig={detailedInstrumentConfig}
                                    underlyingCcy={underlyingCcy}
                                    isCFD={isCFD}
                                    isRollingFiatFX={isRollingFiatFX}
                                    isContent
                                />
                            </div>
                        )}
                        {instrumentInfoContentTab === InstrumentInfoTab['Trade Info'] && (
                            <div
                                className={classNames('flex p-2 pt-1 justify-between w-fit', {
                                    ['flex-wrap gap-1']: props.flexWrap,
                                    ['flex-wrap xsm:flex-nowrap gap-1 xsm:gap-3']: !props.flexWrap
                                })}>
                                <TradeInfo
                                    publicInstrumentConfig={publicInstrumentConfig}
                                    marginRequirement={marginRequirement}
                                    entityType={entityType}
                                    ccy1Config={ccy1Config}
                                    ccy2Config={ccy2Config}
                                    isContent
                                />
                            </div>
                        )}
                    </div>
                </div>
                <Divider />
                <div className="flex flex-row justify-between text-xs items-center p-2">
                    <div className="justify-end flex space-x-2">
                        <button
                            type="button"
                            className="rounded-sm p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                            onClick={onClose}>
                            {lang.common.back}
                        </button>
                        {onRemove && watchListId && (
                            <button
                                type="button"
                                className={classNames('rounded-sm p-2 px-4 whitespace-nowrap', {
                                    ['bg-brand-red hover:bg-brand-red-light']: option?.added,
                                    ['bg-brand-primary hover:bg-brand-primary-light']: !option?.added
                                })}
                                onClick={() => {
                                    if (option?.added) {
                                        onRemove(option.market as any);
                                        dispatch(
                                            removeSubscription({
                                                id: watchListId,
                                                market: option.market as any
                                            })
                                        );
                                        onClose();
                                    } else {
                                        if (option?.market) onSubmit(option.market);
                                        onClose();
                                    }
                                }}>
                                {option?.added ? lang.common.removeFromWatchlist : lang.common.addToWatchlist}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default InstrumentInfoContent;
